import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Option } from '../../types/Option/Option';
import SelectArrow from './SelectArrow';
import useOnClickOutside from '../../hooks/useOnClickOutside/useOnClickOutside';

import styles from './Select.module.scss';

interface SelectProps {
    label?: string;
    value?: string | number | null;
    options: Option[];
    onChange: (optionKey: string | number | null) => void;
    placeholder?: string;
    dropdownDistance?: number;
}

const Select = ({
    label,
    value,
    options,
    onChange,
    placeholder = 'Any',
    dropdownDistance = 5,
}: SelectProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
    const selectedValue = useMemo(() => {
        if (value === undefined || value === null) return placeholder;
        const selectedOption = options.find((option) => option.key === value);
        if (!selectedOption) return placeholder;
        return selectedOption.displayName;
    }, [value, options, placeholder]);

    const handleOnClick = useCallback(
        (optionKey: string | number | null) => {
            setDropdownVisible(false);
            onChange(optionKey);
        },
        [onChange]
    );

    useOnClickOutside(ref, () => setDropdownVisible(false));

    const selectHeight = useMemo(() => {
        if (!ref || !ref.current) return 0;
        return ref.current.getBoundingClientRect().height;
    }, [ref]);

    return (
        <div className={styles.root} ref={ref} data-cy="select">
            {label ? <span className={styles.label}>{label}</span> : null}
            <div
                className={`${styles.select} ${
                    dropdownVisible ? styles.openedSelect : ''
                }`}
                data-testid="select"
                onClick={() => setDropdownVisible(!dropdownVisible)}
            >
                {selectedValue}
                <SelectArrow />
            </div>
            {dropdownVisible ? (
                <div
                    data-testid="dropdown"
                    className={styles.dropdown}
                    style={{ top: selectHeight + dropdownDistance }}
                >
                    <div
                        key={'empty'}
                        onClick={() => handleOnClick(null)}
                        className={styles.option}
                    >
                        {placeholder}
                    </div>
                    {options.map((option) => {
                        return (
                            <div
                                className={styles.option}
                                key={option.key}
                                onClick={() => handleOnClick(option.key)}
                            >
                                {option.displayName}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

export default Select;
